import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isSessionIdleSuppressed } from '../../utils/sessionActivity';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireProfileComplete?: boolean;
  adminOnly?: boolean;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireProfileComplete = false,
  adminOnly = false,
  allowedRoles
}) => {
  const { isAuthenticated, user, profileComplete, profileStatus, checkProfileStatus, logout } = useAuth();
  const location = useLocation();
  const IDLE_TIMEOUT_MS = 3 * 60 * 1000;
  const lastActivity = Number(localStorage.getItem('last_activity_ts') || 0);
  const sessionExpired =
    isAuthenticated &&
    lastActivity > 0 &&
    Date.now() - lastActivity >= IDLE_TIMEOUT_MS &&
    !isSessionIdleSuppressed();

  useEffect(() => {
    if (isAuthenticated && !profileStatus) {
      checkProfileStatus();
    }
  }, [isAuthenticated, profileStatus, checkProfileStatus]);

  useEffect(() => {
    if (sessionExpired) {
      logout({ silent: true });
    }
  }, [logout, sessionExpired]);

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated || sessionExpired) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si es solo para admins y no es admin
  if (adminOnly && !['admin', 'manager'].includes(user?.role || '')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (allowedRoles?.length && !allowedRoles.includes(user?.role || '')) {
    const fallback = user?.role === 'manager' || user?.role === 'admin'
      ? '/admin/dashboard'
      : user?.role === 'developer'
        ? '/developer/panel'
        : '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  // Si el usuario está PENDING_APPROVAL, mostrar mensaje apropiado
  if (user && (user as any).status === 'PENDING_APPROVAL') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h2>Cuenta Pendiente de Aprobación</h2>
        <p>Tu cuenta está siendo revisada por nuestro equipo. Te notificaremos por correo cuando sea aprobada.</p>
        <button 
          onClick={() => window.location.href = '/logout'}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Cerrar Sesión
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
